import type { CheckoutLanguage } from '../components/checkoutLabels';

// The words of paying by card, in each language a store may be served in: the choice at checkout, the label a
// receipt prints, and what the thank-you page says while a payment is checked or when it did not go through.

/** Every sentence a card payment prints. */
export type PaymentWords = {
  payment: string; cash: string; card: string; cardNote: string;
  checking: string; unpaidTitle: string; unpaidText: string; payAgain: string; failed: string;
};

/** The words in English. */
const EN: PaymentWords = {
  payment: 'Payment', cash: 'Cash on delivery', card: 'Card', cardNote: 'You pay securely on Stripe’s page.',
  checking: 'Checking your payment…', unpaidTitle: 'Your card payment was not completed.',
  unpaidText: 'Your order {number} is saved. Pay it now by card, or we will call you to arrange it.', payAgain: 'Pay by card',
  failed: 'Your payment could not be checked just now. Please refresh this page.'
};

/** The words in French. */
const FR: PaymentWords = {
  payment: 'Paiement', cash: 'Paiement à la livraison', card: 'Carte bancaire', cardNote: 'Vous payez en toute sécurité sur la page de Stripe.',
  checking: 'Vérification de votre paiement…', unpaidTitle: 'Votre paiement par carte n’a pas abouti.',
  unpaidText: 'Votre commande {number} est enregistrée. Payez-la maintenant par carte, ou nous vous appellerons.', payAgain: 'Payer par carte',
  failed: 'Votre paiement n’a pas pu être vérifié pour le moment. Actualisez cette page.'
};

/** The words in Arabic. */
const AR: PaymentWords = {
  payment: 'الدفع', cash: 'الدفع عند الاستلام', card: 'البطاقة البنكية', cardNote: 'تدفع بأمان على صفحة Stripe.',
  checking: 'جارٍ التحقق من الدفع…', unpaidTitle: 'لم يكتمل الدفع بالبطاقة.',
  unpaidText: 'تم حفظ طلبك {number}. ادفع الآن بالبطاقة، أو سنتصل بك لترتيب ذلك.', payAgain: 'الدفع بالبطاقة',
  failed: 'تعذر التحقق من الدفع الآن. يرجى تحديث هذه الصفحة.'
};

/** Every language's words. */
const WORDS: Readonly<Record<CheckoutLanguage, PaymentWords>> = { en: EN, fr: FR, ar: AR };

// Picks the card payment's words for a store's language, English when it has chosen none.
export const paymentWords = (language: CheckoutLanguage | null | undefined): PaymentWords => WORDS[language ?? 'en'];
